import { config } from "../constants";
import { authHeader } from "../helpers";
import { handleResponse } from "./handle-response";
import { fetchWithTimeout } from "./fetch-with-timeout";

export const getPDT = (quarter_name) => {
  const requestOptions = {
    method: "GET",
    headers: { ...authHeader(), "Content-Type": "application/json" },
  };

  if (!quarter_name) {
    return fetch(`${config.API_URL}/template/pdt/`, requestOptions).then(
      handleResponse,
    );
  } else {
    return fetch(
      `${config.API_URL}/template/pdt/?quarter=` + quarter_name,
      requestOptions,
    ).then(handleResponse);
  }
};

export const postPDT = (data) => {
  const requestOptions = {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };

  return fetch(`${config.API_URL}/template/pdt/`, requestOptions).then(
    handleResponse,
  );
};

export const adminGetPDT = (id, quarter_name) => {
  const requestOptions = {
    method: "GET",
    headers: { ...authHeader(), "Content-Type": "application/json" },
  };

  if (!quarter_name) {
    return fetch(`${config.API_URL}/template/pdt/${id}/`, requestOptions).then(
      handleResponse,
    );
  } else {
    return fetch(
      `${config.API_URL}/template/pdt/${id}/?quarter=` + quarter_name,
      requestOptions,
    ).then(handleResponse);
  }
};

export const adminPostPDT = (id, data, quarter_name) => {
  const requestOptions = {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };

  if (!quarter_name) {
    return fetch(`${config.API_URL}/template/pdt/${id}/`, requestOptions).then(
      handleResponse,
    );
  } else {
    return fetch(
      `${config.API_URL}/template/pdt/${id}/?quarter=` + quarter_name,
      requestOptions,
    ).then(handleResponse);
  }
};

export const submitPDT = ({ message }) => {
  const requestOptions = {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
    body: JSON.stringify({ message }),
  };

  return fetch(`${config.API_URL}/template/pdt/submit/`, requestOptions).then(
    handleResponse,
  );
};

export const approvePDT = ({ partner_id, message, is_approved }) => {
  const requestOptions = {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
    body: JSON.stringify({ partner_id, message, is_approved }),
  };

  return fetch(`${config.API_URL}/template/pdt/message/`, requestOptions).then(
    handleResponse,
  );
};

export const markPDTMessageRead = (type) => {
  const requestOptions = {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
  };

  return fetch(
    `${config.API_URL}/template/pdt/inbox/${type}`,
    requestOptions,
  ).then(handleResponse);
};

export const getPDTReport = (data, signal) => {
  // Extract pagination parameters for query string
  const { limit, offset, ...bodyData } = data;

  const requestOptions = {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
    body: JSON.stringify(bodyData),
    signal,
  };

  // Build query parameters for pagination
  const searchParams = new URLSearchParams();
  if (limit !== undefined && limit !== null) {
    searchParams.append('limit', limit);
  }
  if (offset !== undefined && offset !== null) {
    searchParams.append('offset', offset);
  }

  const queryString = searchParams.toString();
  const url = `${config.API_URL}/report/pdt/${queryString ? `?${queryString}` : ''}`;

  return fetchWithTimeout(url, requestOptions).then(handleResponse);
};

export const downloadPDTReport = () => {
  const requestOptions = {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
    body: JSON.stringify({}),
  };

  return fetch(`${config.API_URL}/report/pdt/download/`, requestOptions)
    .then((response) => response.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `sample.${this.state.file}`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    })
    .catch((error) => {
      console.log(error);
    });
};

export const downloadpdtReport = (quarter, requestObj) => {
  requestObj["quarter"] = quarter;
  const requestOptions = {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
    body: JSON.stringify(requestObj),
    responseType: "blob",
  };

  return fetch(`${config.API_URL}/report/pdt/download/`, requestOptions).then(
    (response) => response.blob(),
  );
};


// ─── Diagnostics PDT ────────────────────────────────────────────────────────

/**
 * Save per-stage progress for a Diagnostics product (partner).
 * @param {number} activeProductId
 * @param {string} quarterName  e.g. "Oct-Dec 2025"
 * @param {Array}  stages       [{ stage_id, status, start_date, end_date, notes }] (status = the stage's own status)
 * @param {string} [productStatus] product-level status
 */
export const postDiagnosticsPDT = (activeProductId, quarterName, stages, productStatus = null) => {
  const requestOptions = {
    method: 'POST',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      quarter_name: quarterName,
      stages,
      // product-level status; omitted when not set so the backend leaves it untouched
      ...(productStatus ? { product_status: productStatus } : {}),
    }),
  };
  return fetch(
    `${config.API_URL}/template/pdt/diagnostics/${activeProductId}/`,
    requestOptions,
  ).then(handleResponse);
};

/**
 * Save per-stage progress for a Diagnostics product (admin).
 */
export const adminPostDiagnosticsPDT = (activeProductId, quarterName, stages, productStatus = null) => {
  const requestOptions = {
    method: 'POST',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      quarter_name: quarterName,
      stages,
      // product-level status; omitted when not set so the backend leaves it untouched
      ...(productStatus ? { product_status: productStatus } : {}),
    }),
  };
  return fetch(
    `${config.API_URL}/template/pdt/diagnostics/admin/${activeProductId}/`,
    requestOptions,
  ).then(handleResponse);
};

// ─── Diagnostics Deliverables ────────────────────────────────────────────────

/**
 * Upload one or more files for a Diagnostics stage.
 */
export const uploadDiagnosticsDeliverable = (
  files,
  activeProductId,
  stageId,
  quarter,
) => {
  const formData = new FormData();
  const filesArray = Array.isArray(files) ? files : [files];
  filesArray.forEach((file) => formData.append('file', file));
  formData.append('active_product_id', activeProductId);
  formData.append('stage_id', stageId);
  formData.append('quarter', quarter);

  const requestOptions = {
    method: 'POST',
    headers: authHeader(),
    body: formData,
  };
  return fetch(
    `${config.API_URL}/template/pdt/diagnostics/deliverables/upload/`,
    requestOptions,
  ).then(handleResponse);
};

/**
 * Fetch deliverables for a Diagnostics product.
 */
export const getDiagnosticsDeliverables = (
  activeProductId,
  stageId = null,
  quarter = null,
) => {
  const params = new URLSearchParams();
  params.append('active_product_id', activeProductId);
  if (stageId) params.append('stage_id', stageId);
  if (quarter) params.append('quarter', quarter);

  const requestOptions = {
    method: 'GET',
    headers: authHeader(),
  };
  return fetch(
    `${config.API_URL}/template/pdt/diagnostics/deliverables/?${params.toString()}`,
    requestOptions,
  ).then(handleResponse);
};

/**
 * Delete a Diagnostics deliverable by its ID.
 */
export const deleteDiagnosticsDeliverable = (fileId) => {
  const requestOptions = {
    method: 'DELETE',
    headers: authHeader(),
  };
  return fetch(
    `${config.API_URL}/template/pdt/diagnostics/deliverables/${fileId}/`,
    requestOptions,
  ).then(handleResponse);
};
